import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import StickyFooter from './components/StickyFooter';
import { companyName } from '../defaults/AppDefaults';
import backgroundImg from '../images/pexels-gromakova-92378617-9146943.jpg';
import {
  INDEPENDENT_BARBER_PLANS,
  SHOP_OWNER_PLANS,
} from '../defaults/SubscriptionPlans';
import featuresData from '../FeaturesData.json';

const FEATURE_ICONS = [
  <EventAvailableRoundedIcon sx={{ fontSize: 30 }} />,
  <PeopleAltRoundedIcon sx={{ fontSize: 30 }} />,
  <CampaignRoundedIcon sx={{ fontSize: 30 }} />,
];

const ALL_PLANS = [...INDEPENDENT_BARBER_PLANS, ...SHOP_OWNER_PLANS];

/**
 * Public Features & Pricing page aimed at converting visitors into
 * registered users. Unlike Subscriptions.tsx (which manages an existing
 * user's live subscription), this page is informational only - every
 * call-to-action routes visitors to the registration flow.
 */
export default function FeaturesPricing() {
  const navigate = useNavigate();

  const handleGetStarted = () => navigate('/get-started');

  return (
    <div style={{ backgroundColor: '#0f1013' }}>
      {/* Hero */}
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(8,9,12,0.82), rgba(8,9,12,0.9)), url(${backgroundImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          textAlign: 'center',
          py: 10,
          px: 2,
          mt: '85px',
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold', mb: 2 }}
        >
          Features & Pricing
        </Typography>
        <Chip
          label="✨ 3-Month Free Trial on Every Plan"
          sx={{
            bgcolor: '#dd8709',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '0.95rem',
            px: 1.5,
            py: 2.5,
            mb: 3,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            mb: 4,
            maxWidth: 640,
            mx: 'auto',
            color: 'rgba(255,255,255,0.75)',
          }}
        >
          Everything you need to manage bookings, grow your client base, and run
          your barbering business — all in one place.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleGetStarted}
          sx={{
            bgcolor: '#dd8709',
            fontWeight: 'bold',
            textTransform: 'none',
            px: 4,
            py: 1.5,
            '&:hover': { bgcolor: '#b86e08' },
          }}
        >
          Get Started
        </Button>
      </Box>

      {/* Features */}
      <Box sx={{ py: 9, bgcolor: '#0f1013' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 6,
              color: '#fff',
            }}
          >
            Built for Barbers and Shop Owners
          </Typography>
          <Grid container spacing={4}>
            {featuresData.map((feature, index) => (
              <Grid item xs={12} md={4} key={feature.title}>
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Box
                    sx={{
                      width: 68,
                      height: 68,
                      borderRadius: '50%',
                      mx: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      background:
                        'linear-gradient(135deg, #dd8709 0%, #b86e08 100%)',
                      boxShadow: '0 8px 20px rgba(221,135,9,0.35)',
                    }}
                  >
                    {FEATURE_ICONS[index % FEATURE_ICONS.length]}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'GFS Didot',
                      fontWeight: 'bold',
                      mt: 2.5,
                      color: '#fff',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 1,
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '1.05rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {feature.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing */}
      <Box sx={{ bgcolor: '#16171c', py: 9 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 1,
              color: '#fff',
            }}
          >
            Plans for Every Barber
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.65)' }}
          >
            Whether you're an independent barber or run a shop with a team, we
            have a plan to match. Try any plan free for 3 months — no
            commitment, cancel anytime.
          </Typography>
          <Grid container spacing={4} alignItems="stretch">
            {ALL_PLANS.map((plan) => (
              <Grid item xs={12} sm={6} md={3} key={plan.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    bgcolor: '#1b1c22',
                    border: plan.tag
                      ? '2px solid #dd8709'
                      : '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {plan.tag && (
                      <Chip
                        label={plan.tag}
                        size="small"
                        sx={{
                          bgcolor: plan.tagColor ?? '#dd8709',
                          color: '#fff',
                          fontWeight: 'bold',
                          mb: 1,
                          alignSelf: 'flex-start',
                        }}
                      />
                    )}
                    {plan.trialDays > 0 && (
                      <Chip
                        label={`${Math.round(
                          plan.trialDays / 30
                        )}-Month Free Trial`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(221,135,9,0.15)',
                          color: '#dd8709',
                          fontWeight: 'bold',
                          mb: 1,
                          alignSelf: 'flex-start',
                          border: '1px solid #dd8709',
                        }}
                      />
                    )}
                    <Typography
                      variant="overline"
                      sx={{ color: 'rgba(255,255,255,0.55)' }}
                    >
                      {plan.type === 'BARBER'
                        ? 'Independent Barber'
                        : 'Shop Owner'}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: 'GFS Didot',
                        fontWeight: 'bold',
                        color: '#fff',
                      }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 'bold', my: 2, color: '#fff' }}
                    >
                      £{plan.price.toFixed(2)}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.55)' }}
                      >
                        /mo
                      </Typography>
                    </Typography>
                    <List dense sx={{ flexGrow: 1 }}>
                      {plan.features.map((feature) => (
                        <ListItem key={feature} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleRoundedIcon
                              sx={{ color: '#4caf50', fontSize: 20 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              sx: { color: 'rgba(255,255,255,0.8)' },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleGetStarted}
                      sx={{
                        mt: 2,
                        bgcolor: '#dd8709',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#b86e08' },
                      }}
                    >
                      Start Free Trial
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ py: 9, textAlign: 'center', px: 2, bgcolor: '#0f1013' }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'GFS Didot',
            fontWeight: 'bold',
            mb: 2,
            color: '#fff',
          }}
        >
          Ready to grow your business?
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 4,
            maxWidth: 560,
            mx: 'auto',
            color: 'rgba(255,255,255,0.65)',
          }}
        >
          Join Barbers Limited today and start managing your bookings, clients,
          and marketing in minutes — free for your first 3 months.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleGetStarted}
          sx={{
            bgcolor: '#dd8709',
            fontWeight: 'bold',
            textTransform: 'none',
            px: 4,
            py: 1.5,
            '&:hover': { bgcolor: '#b86e08' },
          }}
        >
          Start Your 3-Month Free Trial
        </Button>
      </Box>

      <StickyFooter {...companyName} />
    </div>
  );
}
